import { faWallet } from '@fortawesome/pro-duotone-svg-icons'
import { Box, Card, Divider, Stack } from '@mui/material'
import { memo } from 'react'

import LogoContainer from 'src/components/shared/logo/logoContainer'
import StakingProviders from 'src/components/overview/StakingProviders'
import Button from 'src/components/shared/button/Button'
import SubmitButton from 'src/components/shared/button/SubmitButton'
import FaIcon from 'src/components/shared/faIcon/FaIcon'
import CustomForm from 'src/components/shared/inputs/CustomForm'
import CustomRadio from 'src/components/shared/inputs/CustomRadio'
import CustomTextField from 'src/components/shared/inputs/CustomTextField'
import CardHeading from 'src/components/shared/text/CardHeading'
import DisplayLabel from 'src/components/shared/text/DisplayLabel'
import Text from 'src/components/shared/text/Text'
import {
  formatCurrency,
  formatCurrencyShort,
  roundDownDecimals
} from 'src/util/number.helpers'
import { CURRENCIES } from 'src/util/staking/staking.helpers'
import { formatTicks } from 'src/util/string.helpers'
import { maskWalletAddress } from 'src/util/wallet/wallet.helpers'
import { STAKING_PROFILE_DISPATCH } from '../../store/reducers/staking/staking'
import LabelValue from '../common/LabelValue'

const StakingForm = ({
  t,
  dispatchAction,
  onSubmit,
  connectedWallet,
  stakingProfile: {
    availableQBX,
    stakeAmount,
    error,
    priceOfQBX,
    sdkProcessing
  },
  onWalletClick,
  hideWallet
}) => {
  const availableShort = availableQBX ? roundDownDecimals(availableQBX, 3) : 0
  const handleClickMax = () => {
    dispatchAction(STAKING_PROFILE_DISPATCH.ERROR, {})
    dispatchAction(STAKING_PROFILE_DISPATCH.STAKE_AMOUNT, availableShort)
  }
  return (
    <Card>
      <Box p={3}>
        <CardHeading title={t('brand:staking.stake-more')} />
        <CustomForm onFormSubmit={onSubmit}>
          <Stack spacing={3}>
            {!hideWallet && (
              <>
                {connectedWallet ? (
                  <CustomTextField
                    label={t('brand:staking.connected-wallet')}
                    value={maskWalletAddress(connectedWallet)}
                    disabled
                    multiline
                    startIcon={<FaIcon icon={faWallet} />}
                  />
                ) : (
                  <SubmitButton
                    type='button'
                    label={t('wallet.connect-wallet')}
                    fullWidth
                    onClick={onWalletClick}
                    loading={sdkProcessing}
                  />
                )}
              </>
            )}
            <Stack spacing={1.5}>
              <DisplayLabel
                title={t('brand:staking.available')}
                label={formatTicks(availableShort)}
                helpText={formatCurrency(availableQBX)}
              />
              <CustomTextField
                label={CURRENCIES.QBX}
                error={t(error[STAKING_PROFILE_DISPATCH.STAKE_AMOUNT])}
                value={stakeAmount}
                onChange={(value) =>
                  dispatchAction(STAKING_PROFILE_DISPATCH.STAKE_AMOUNT, value)
                }
                maxLength={18}
                startIcon={
                  <LogoContainer
                    src='/assets/icons/qbx.png'
                    noLink
                    sx={{ width: 28 }}
                  />
                }
                type='number'
                endIcon={
                  <Button
                    label={t('brand:staking.max')}
                    onClick={handleClickMax}
                    size='small'
                    disabled={!availableQBX > 0}
                  />
                }
              />
            </Stack>
            {!hideWallet && (
              <LabelValue
                label={`${t('brand:staking.value-in-usd')}: `}
                value={`$${stakeAmount && priceOfQBX > 0 ? formatCurrencyShort(parseFloat(stakeAmount) * priceOfQBX) : 0}`}
              />
            )}
            <Stack spacing={1}>
              <Text
                variant='body1'
                label={t('brand:staking.staking-duration')}
                fontWeight={700}
              />
              <CustomRadio
                checked
                label={t('brand:staking.bonus-perks')}
                color='primary'
                size='small'
              />
            </Stack>
          </Stack>
          <Stack spacing={3} sx={{ mt: 3, alignItems: 'flex-end' }}>
            <SubmitButton
              label={t('brand:staking.stake')}
              disabled={!availableQBX > 0}
            />
          </Stack>
        </CustomForm>
      </Box>
      <Divider sx={{ borderStyle: 'dashed' }} />
      <Box p={3}>
        <StakingProviders title={t('brand:staking.buy-more')} />
      </Box>
    </Card>
  )
}

export default memo(StakingForm)
